import React, { useEffect, useState } from "react";
import { YouTubeApi } from "@/lib/api";
import { Play, Youtube } from "lucide-react";

export default function YouTubeSection() {
    const [videos, setVideos] = useState([]);
    useEffect(() => {
        YouTubeApi.list().then(setVideos).catch(() => {});
    }, []);

    const [featured, ...rest] = videos;

    return (
        <section id="video" className="relative z-10" data-testid="youtube-section">
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-16 md:pt-24">
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <div className="overline-cobalt mb-3">§ Podcast</div>
                        <h2
                            className="font-display"
                            style={{
                                fontSize: "clamp(36px, 5vw, 64px)",
                                lineHeight: 0.98,
                                color: "var(--ink)",
                            }}
                        >
                            Long-form{" "}
                            <span
                                style={{
                                    fontFamily: "'Fraunces', serif",
                                    fontStyle: "italic",
                                    fontWeight: 400,
                                    color: "var(--cobalt)",
                                }}
                            >
                                conversations
                            </span>
                            .
                        </h2>
                    </div>
                    <a
                        href="https://www.youtube.com/@AtlasBerry008"
                        target="_blank"
                        rel="noreferrer"
                        className="btn-ghost hidden md:inline-flex"
                        data-testid="youtube-subscribe-btn"
                    >
                        <Youtube size={14} /> Subscribe
                    </a>
                </div>

                {videos.length === 0 ? (
                    <EmptyYouTube />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                        {featured && (
                            <VideoCard video={featured} featured className="md:col-span-8 md:row-span-2 min-h-[420px]" />
                        )}
                        {rest.slice(0, 4).map((v) => (
                            <VideoCard key={v.id} video={v} className="md:col-span-4 min-h-[200px]" />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

function VideoCard({ video, className = "", featured = false }) {
    return (
        <a
            href={video.url}
            target="_blank"
            rel="noreferrer"
            className={`group relative overflow-hidden panel ${className}`}
            data-testid={`video-card-${video.video_id}`}
        >
            <img
                src={video.thumbnail}
                alt={video.title}
                className="absolute inset-0 w-full h-full object-cover transition-all duration-500 group-hover:scale-[1.03]"
                style={{ filter: "grayscale(70%) contrast(0.95)" }}
                loading="lazy"
                onMouseEnter={(e) => (e.currentTarget.style.filter = "grayscale(0%)")}
                onMouseLeave={(e) => (e.currentTarget.style.filter = "grayscale(70%) contrast(0.95)")}
            />
            <div
                className="absolute inset-0"
                style={{
                    background: "linear-gradient(180deg, rgba(10,13,18,0.25) 0%, rgba(10,13,18,0.85) 100%)",
                }}
            />
            <div className="relative p-6 md:p-8 h-full flex flex-col justify-between">
                <div className="flex items-center justify-between">
                    <span
                        className="font-mono-tech uppercase"
                        style={{
                            fontSize: 10,
                            letterSpacing: "0.28em",
                            color: "var(--cobalt-soft)",
                        }}
                    >
                        {featured ? "[Featured · 01]" : "[Clip]"}
                    </span>
                    <span
                        className="w-10 h-10 flex items-center justify-center border"
                        style={{
                            borderColor: "rgba(255,255,255,0.4)",
                            background: "rgba(10,13,18,0.4)",
                            color: "white",
                        }}
                    >
                        <Play size={14} fill="currentColor" />
                    </span>
                </div>
                <h3
                    style={{
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 900,
                        fontSize: featured ? 32 : 18,
                        color: "white",
                        letterSpacing: "-0.02em",
                        lineHeight: 1.1,
                    }}
                >
                    {video.title}
                </h3>
            </div>
        </a>
    );
}

function EmptyYouTube() {
    return (
        <div className="grid md:grid-cols-2 gap-4" data-testid="youtube-empty">
            <div className="panel p-10 md:p-14 flex flex-col justify-between min-h-[340px]">
                <div className="overline-cobalt">[Transmission · Pending]</div>
                <div>
                    <h3
                        style={{
                            fontFamily: "'Inter', sans-serif",
                            fontWeight: 900,
                            fontSize: 40,
                            color: "var(--ink)",
                            letterSpacing: "-0.025em",
                            lineHeight: 1,
                        }}
                        className="mb-5"
                    >
                        The channel launches{" "}
                        <span
                            style={{
                                fontFamily: "'Fraunces', serif",
                                fontStyle: "italic",
                                fontWeight: 400,
                                color: "var(--cobalt)",
                            }}
                        >
                            soon.
                        </span>
                    </h3>
                    <p
                        style={{
                            color: "var(--ink-2)",
                            fontSize: 14,
                            lineHeight: 1.65,
                            maxWidth: 440,
                        }}
                    >
                        Subscribe on YouTube to catch the first interview with a stealth
                        frontier founder. Admins can sync videos once the channel goes live.
                    </p>
                    <a
                        href="https://www.youtube.com/@AtlasBerry008"
                        target="_blank"
                        rel="noreferrer"
                        className="btn-primary mt-6"
                        data-testid="youtube-empty-cta"
                    >
                        <Youtube size={14} /> Subscribe on YouTube
                    </a>
                </div>
            </div>
            <div className="panel relative overflow-hidden min-h-[340px]">
                <img
                    src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80"
                    alt="Frontier broadcast"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ filter: "grayscale(70%) brightness(0.9)" }}
                />
                <div
                    className="absolute inset-0"
                    style={{
                        background: "linear-gradient(180deg, rgba(10,13,18,0.2), rgba(10,13,18,0.8))",
                    }}
                />
                <div className="absolute bottom-6 left-6 overline" style={{ color: "rgba(255,255,255,0.75)" }}>
                    @AtlasBerry008
                </div>
            </div>
        </div>
    );
}
